import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Mail, Check, Info } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { C } from "./PulseLayout";

// Set real store URLs only after the listings are approved and live. Badges stay hidden while null.
export const STORE_LINKS = { appStore: null, googlePlay: null };
const LAUNCH_API = `${process.env.REACT_APP_BACKEND_URL}/api/pulse/launch`;

export const StoreBadges = ({ className = "" }) => {
  const items = [["appStore", "Download on the", "App Store"], ["googlePlay", "Get it on", "Google Play"]].filter(([k]) => STORE_LINKS[k]);
  if (!items.length) return null;
  return (
    <div className={`flex flex-wrap gap-3 ${className}`} data-testid="pulse-store-badges">
      {items.map(([k, small, big]) => (
        <a key={k} href={STORE_LINKS[k]} target="_blank" rel="noreferrer" className="rounded-xl border px-4 py-2 leading-tight" style={{ borderColor: C.silver, background: "#000" }} data-testid={`pulse-store-${k}`}>
          <span className="block text-[10px]">{small}</span><span className="block font-semibold">{big}</span>
        </a>
      ))}
    </div>
  );
};

export const ScreenLightbox = ({ screens, index, onChange, returnRef }) => {
  const open = index !== null;
  const s = open ? screens[index] : null;
  const go = (d) => onChange((index + d + screens.length) % screens.length);
  const onKey = (e) => {
    if (e.key === "ArrowRight") { e.preventDefault(); go(1); }
    if (e.key === "ArrowLeft") { e.preventDefault(); go(-1); }
  };
  const Nav = ({ d, Icon, label }) => (
    <button type="button" onClick={() => go(d)} aria-label={label} className="h-11 w-11 shrink-0 inline-flex items-center justify-center rounded-full border transition-colors hover:bg-[#19354D]" style={{ borderColor: C.slate, color: C.cyan }} data-testid={`pulse-lightbox-${d < 0 ? "prev" : "next"}`}><Icon className="h-5 w-5" /></button>
  );
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onChange(null)}>
      <DialogContent onKeyDown={onKey} onCloseAutoFocus={(e) => { e.preventDefault(); returnRef?.current?.focus(); }} className="outline-none focus:outline-none focus-visible:outline-none max-w-none w-auto border-0 bg-transparent p-0 shadow-none text-white [&>button]:text-white [&>button]:opacity-90 [&>button]:-top-10 [&>button]:right-0" data-testid="pulse-lightbox">
        {s && (
          <div className="flex flex-col items-center gap-4">
            <DialogTitle className="sr-only">{s.label}</DialogTitle>
            <DialogDescription className="sr-only">Screenshot {index + 1} of {screens.length}. Use the left and right arrow keys to move between screenshots.</DialogDescription>
            <div className="flex items-center gap-3 sm:gap-5">
              <Nav d={-1} Icon={ChevronLeft} label="Previous screenshot" />
              <img src={s.src} alt={s.alt} className="h-[78vh] max-h-[1300px] w-auto max-w-[calc(100vw-8.5rem)] object-contain rounded-[1.6rem] border" style={{ borderColor: C.slate }} data-testid="pulse-lightbox-image" />
              <Nav d={1} Icon={ChevronRight} label="Next screenshot" />
            </div>
            <p className="text-sm" style={{ color: C.silver }} data-testid="pulse-lightbox-caption">{index + 1} / {screens.length} · {s.label}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const LaunchSignup = () => {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [state, setState] = useState({ status: "idle", msg: "" });
  const [cfg, setCfg] = useState({ active: false, consent_version: null });
  const trap = useRef(null);
  const LAUNCH_SIGNUP_ACTIVE = cfg.active;
  useEffect(() => {
    fetch(`${LAUNCH_API}/status`).then(r => r.ok ? r.json() : null).then(d => d && setCfg(d)).catch(() => {});
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (trap.current?.value || state.status === "sending") return;
    if (!EMAIL_RE.test(email.trim())) return setState({ status: "error", msg: "Please enter a valid email address." });
    if (!consent) return setState({ status: "error", msg: "Please tick the box to agree to receive PULSE launch updates." });
    if (!LAUNCH_SIGNUP_ACTIVE) return;
    setState({ status: "sending", msg: "Registering…" });
    try {
      const res = await fetch(`${LAUNCH_API}/subscribe`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), consent, consent_version: cfg.consent_version, company_website: "" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setState({ status: "error", msg: typeof data.detail === "string" ? data.detail : "Something went wrong. Please try again." });
      setState({ status: "success", msg: data.message });
      if (data.status === "subscribed") { setEmail(""); setConsent(false); }
    } catch {
      setState({ status: "error", msg: "We couldn't reach the server. Please try again." });
    }
  };
  return (
    <section className="max-w-6xl mx-auto px-5 py-16" data-testid="pulse-launch-signup">
      <div className="rounded-3xl border p-8 md:p-12 grid lg:grid-cols-2 gap-10 items-center" style={{ borderColor: C.slate, background: C.ocean }}>
        <div>
          <Mail className="h-8 w-8 mb-4" style={{ color: C.cyan }} />
          <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-3">Be Among the First to Experience PULSE.</h2>
          <p className="text-sm md:text-base" style={{ color: C.silver }}>PULSE is preparing to launch in the UK. Sign up to hear when it's available.</p>
        </div>
        <form onSubmit={submit} noValidate className="space-y-4" data-testid="pulse-signup-form">
          {!LAUNCH_SIGNUP_ACTIVE && (
            <p className="flex items-start gap-2 rounded-xl border px-4 py-3 text-sm" style={{ borderColor: C.slate, color: C.cyan }} data-testid="pulse-signup-not-open">
              <Info className="h-4 w-4 mt-0.5 shrink-0" />Launch updates sign-up is coming soon and is not open yet. No email addresses are being collected.
            </p>
          )}
          <fieldset disabled={!LAUNCH_SIGNUP_ACTIVE} className="space-y-4 disabled:opacity-60">
          <label htmlFor="pulse-signup-email" className="block text-sm font-medium">Email address</label>
          <input id="pulse-signup-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={254}
            aria-invalid={state.status === "error"} aria-describedby="pulse-signup-feedback"
            className="w-full rounded-xl border px-4 py-3 bg-transparent outline-none transition-colors focus:border-[#55E4E7]" style={{ borderColor: C.slate, color: C.white }} placeholder="you@example.com" data-testid="pulse-signup-email" />
          <input ref={trap} type="text" name="company_website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
          <label className="flex items-start gap-3 text-sm cursor-pointer" style={{ color: C.silver }}>
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 h-4 w-4 accent-[#55E4E7]" data-testid="pulse-signup-consent" />
            <span>Yes, email me PULSE launch updates from Veyronix Limited. You can unsubscribe at any time. See the <Link to="/pulse/privacy-policy" className="underline" style={{ color: C.cyan }}>PULSE Privacy Policy</Link>.</span>
          </label>
          <button type="submit" className="rounded-full px-7 py-3 font-semibold transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:hover:opacity-100" style={{ background: C.cyan, color: C.navy }} data-testid="pulse-signup-submit">{LAUNCH_SIGNUP_ACTIVE ? "Get Launch Updates" : "Sign-up opening soon"}</button>
          </fieldset>
          <div id="pulse-signup-feedback" role="status" aria-live="polite" className="min-h-[1.25rem]">
            {state.status !== "idle" && (
              <p className="flex items-start gap-2 text-sm" style={{ color: state.status === "error" ? "#F59E9E" : C.cyan }} data-testid={`pulse-signup-${state.status}`}>
                {state.status === "error" ? <Info className="h-4 w-4 mt-0.5 shrink-0" /> : <Check className="h-4 w-4 mt-0.5 shrink-0" />}{state.msg}
              </p>
            )}
          </div>
        </form>
      </div>
    </section>
  );
};
